import { useCallback, useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMagnifyingGlass,
  faXmark,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { useDebouncedValue } from "@/hooks/useDebounce";
import { Checkbox } from "@/components/ui/checkbox/Checkbox";
import {
  AUDITABLE_ENTITY_KINDS,
  ENTITY_KIND_LABELS,
  auditableEntityKey,
  AUDITABLE_ENTITY_KIND_ORDER,
  type AuditableEntityKind,
  type AuditableEntityRow,
} from "@/services/api/guardiansv2/types";
import { defaultGuardiansEntitySearch } from "./defaultGuardiansSearch";
import {
  defaultResolveEntityById,
  isObjectId,
  parseObjectIds,
  type ResolveEntityFn,
} from "./resolveEntityById";

export type EntitySearchFn = (
  query: string,
  kinds: AuditableEntityKind[]
) => Promise<AuditableEntityRow[]>;

export interface EntitySearchProps {
  kinds?: AuditableEntityKind[];
  multi?: boolean;
  selected: AuditableEntityRow[];
  onChange: (selected: AuditableEntityRow[]) => void;
  searchFn?: EntitySearchFn;
  resolveFn?: ResolveEntityFn;
  placeholder?: string;
  minQueryLength?: number;
  /** Debounce delay for text search (ms). ID paste resolves immediately. */
  debounceMs?: number;
  emptyMessage?: string;
  className?: string;
}

function mergeSelected(
  current: AuditableEntityRow[],
  incoming: AuditableEntityRow[],
  multi: boolean
): AuditableEntityRow[] {
  if (!multi) {
    return incoming.length > 0 ? [incoming[0]] : [];
  }
  const keys = new Set(current.map(auditableEntityKey));
  const merged = [...current];
  for (const row of incoming) {
    const key = auditableEntityKey(row);
    if (!keys.has(key)) {
      keys.add(key);
      merged.push(row);
    }
  }
  return merged;
}

/**
 * Reusable debounced entity search with optional multi-select and ID paste.
 */
export function EntitySearch({
  kinds = [...AUDITABLE_ENTITY_KINDS],
  multi = true,
  selected,
  onChange,
  searchFn = defaultGuardiansEntitySearch,
  resolveFn = defaultResolveEntityById,
  placeholder = "Search entities or paste an ID…",
  minQueryLength = 2,
  debounceMs = 1000,
  emptyMessage = "No matching entities.",
  className,
}: EntitySearchProps) {
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebouncedValue(query, debounceMs);
  const [results, setResults] = useState<AuditableEntityRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [idHint, setIdHint] = useState<string | null>(null);

  const selectedKeys = useMemo(
    () => new Set(selected.map(auditableEntityKey)),
    [selected]
  );

  const resolveIds = useCallback(
    async (ids: string[]) => {
      const rows: AuditableEntityRow[] = [];
      for (const id of ids) {
        try {
          const resolved = await resolveFn(id, kinds);
          rows.push(...resolved);
        } catch {
          // skip unresolved ids
        }
      }
      return rows;
    },
    [kinds, resolveFn]
  );

  useEffect(() => {
    const trimmed = debouncedQuery.trim();
    if (!trimmed) {
      setResults([]);
      setError(null);
      setIdHint(null);
      setLoading(false);
      return;
    }

    const pastedIds = parseObjectIds(trimmed);
    const isSingleId = pastedIds.length === 1 && pastedIds[0] === trimmed;
    const isMultiId = pastedIds.length > 1;

    if (isSingleId || isMultiId) {
      let cancelled = false;
      setLoading(true);
      setError(null);
      setIdHint(isMultiId ? `Resolved ${pastedIds.length} IDs` : "Resolved from ID");
      void resolveIds(pastedIds)
        .then((rows) => {
          if (cancelled) return;
          setResults(rows);
          if (rows.length === 0) {
            setError("No auditable entity found for that ID.");
          }
        })
        .catch((err: unknown) => {
          if (!cancelled) {
            setResults([]);
            setError(err instanceof Error ? err.message : "Could not resolve ID");
          }
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
      return () => {
        cancelled = true;
      };
    }

    if (trimmed.length < minQueryLength) {
      setResults([]);
      setError(null);
      setIdHint(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setIdHint(null);
    void searchFn(trimmed, kinds)
      .then((rows) => {
        if (!cancelled) setResults(rows);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setResults([]);
          setError(err instanceof Error ? err.message : "Search failed");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, kinds, minQueryLength, resolveIds, searchFn]);

  const toggleRow = useCallback(
    (row: AuditableEntityRow) => {
      const key = auditableEntityKey(row);
      if (!multi) {
        onChange(selectedKeys.has(key) ? [] : [row]);
        return;
      }
      if (selectedKeys.has(key)) {
        onChange(selected.filter((item) => auditableEntityKey(item) !== key));
        return;
      }
      onChange([...selected, row]);
    },
    [multi, onChange, selected, selectedKeys]
  );

  const removeSelected = useCallback(
    (key: string) => {
      onChange(selected.filter((item) => auditableEntityKey(item) !== key));
    },
    [onChange, selected]
  );

  const handlePaste = useCallback(
    (event: React.ClipboardEvent<HTMLInputElement>) => {
      const text = event.clipboardData.getData("text");
      const ids = parseObjectIds(text);
      if (ids.length === 0) return;

      event.preventDefault();
      setQuery(ids.length === 1 ? ids[0] : ids.join(", "));
      void resolveIds(ids).then((rows) => {
        if (rows.length === 0) {
          setError("No auditable entity found for pasted ID(s).");
          return;
        }
        onChange(mergeSelected(selected, rows, multi));
        setQuery("");
        setResults([]);
        setIdHint(null);
      });
    },
    [multi, onChange, resolveIds, selected]
  );

  const grouped = useMemo(() => {
    const map = new Map<AuditableEntityKind, AuditableEntityRow[]>();
    for (const row of results) {
      const bucket = map.get(row.kind) ?? [];
      bucket.push(row);
      map.set(row.kind, bucket);
    }
    return map;
  }, [results]);

  const orderedKinds = useMemo(
    () => AUDITABLE_ENTITY_KIND_ORDER.filter((kind) => grouped.has(kind)),
    [grouped]
  );

  const showMinLengthHint =
    debouncedQuery.trim().length > 0 &&
    debouncedQuery.trim().length < minQueryLength &&
    !isObjectId(debouncedQuery.trim());

  return (
    <div className={clsx("flex flex-col gap-3", className)} data-testid="entity-search">
      <div className="relative">
        <FontAwesomeIcon
          icon={faMagnifyingGlass}
          className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400 dark:text-slate-400"
        />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onPaste={handlePaste}
          placeholder={placeholder}
          className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none dark:border-slate-600 dark:bg-slate-800/80 dark:text-slate-100 dark:placeholder:text-slate-500"
          data-testid="entity-search-input"
        />
      </div>

      {selected.length > 0 ? (
        <div className="flex flex-wrap gap-2" data-testid="entity-search-selected">
          {selected.map((row) => {
            const key = auditableEntityKey(row);
            return (
              <span
                key={key}
                className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-xs text-slate-800 dark:bg-slate-700/80 dark:text-slate-200"
              >
                <span className="text-slate-500 dark:text-slate-400">
                  {ENTITY_KIND_LABELS[row.kind]}
                </span>
                <span className="max-w-[12rem] truncate">{row.label}</span>
                <button
                  type="button"
                  className="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                  aria-label={`Remove ${row.label}`}
                  onClick={() => removeSelected(key)}
                >
                  <FontAwesomeIcon icon={faXmark} className="h-3 w-3" />
                </button>
              </span>
            );
          })}
        </div>
      ) : null}

      <div className="max-h-64 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50 dark:border-slate-600 dark:bg-slate-800/50">
        {loading ? (
          <p className="px-3 py-4 text-sm text-slate-500 dark:text-slate-400">Searching…</p>
        ) : error ? (
          <p className="px-3 py-4 text-sm text-red-600 dark:text-red-400">{error}</p>
        ) : showMinLengthHint ? (
          <p className="px-3 py-4 text-sm text-slate-500 dark:text-slate-400">
            Type at least {minQueryLength} character{minQueryLength === 1 ? "" : "s"} to search,
            or paste an ObjectId.
          </p>
        ) : results.length === 0 ? (
          <p className="px-3 py-4 text-sm text-slate-500 dark:text-slate-400">
            {debouncedQuery.trim() ? emptyMessage : "Search by name or paste an ObjectId."}
          </p>
        ) : (
          <>
            {idHint ? (
              <p className="border-b border-slate-200 px-3 py-2 text-xs text-slate-500 dark:border-slate-700/60 dark:text-slate-400">
                {idHint}
              </p>
            ) : null}
            <ul className="divide-y divide-slate-700/60">
              {orderedKinds.map((kind) => {
                const rows = grouped.get(kind) ?? [];
                return (
                <li key={kind}>
                  <p className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                    {ENTITY_KIND_LABELS[kind]}
                  </p>
                  <ul>
                    {rows.map((row) => {
                      const key = auditableEntityKey(row);
                      const checked = selectedKeys.has(key);
                      return (
                        <li key={key}>
                          <button
                            type="button"
                            className={clsx(
                              "flex w-full items-start gap-2 px-3 py-2 text-left text-sm transition-colors",
                              checked
                                ? "bg-indigo-50 dark:bg-indigo-900/30"
                                : "hover:bg-slate-100 dark:hover:bg-slate-700/50",
                            )}
                            onClick={() => toggleRow(row)}
                            data-testid={`entity-search-option-${key}`}
                          >
                            <Checkbox checked={checked} readOnly className="mt-0.5" />
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-slate-900 dark:text-slate-100">
                                {row.label}
                              </span>
                              {row.subtitle ? (
                                <span className="block truncate text-xs text-slate-500 dark:text-slate-400">
                                  {row.subtitle}
                                </span>
                              ) : null}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </li>
                );
              })}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}
